import { useEffect, useMemo, useState } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@xiltrion/ui/dropdown-menu';
import { Tree, TreeItem, TreeItemLabel, useTree, hotkeysCoreFeature, selectionFeature, syncDataLoaderFeature } from '@xiltrion/ui/tree';
import { cn } from '@xiltrion/ui/utils';
import type { UserRow } from '@/api/types';
import { AVAILABILITY_LABEL, type UnitNode } from './shared';

export type OrgSelected = { kind: 'unit'; id: string } | null;
export type OrgCreating = { kind: 'unit'; parentId: string | null };

/** Nodo del árbol: área (con sus subáreas) o persona (una misma persona puede estar en varias áreas). */
type OrgNode =
  | { kind: 'root'; name: string; children: string[] }
  | { kind: 'unit'; name: string; children: string[]; unit: UnitNode }
  | { kind: 'person'; name: string; children: string[]; user: UserRow; badges: string[] };

const ROOT = 'raiz';
const INDENT = 20;
const keyOf = (s: NonNullable<OrgSelected>) => `u:${s.id}`;

interface Props {
  units: UnitNode[];
  /** Activas y ordenadas por nombre. */
  activeUsers: UserRow[];
  /** Todas, para contar las personas de cada área. */
  users: UserRow[];
  showPeople: boolean;
  selected: OrgSelected;
  onSelect: (s: NonNullable<OrgSelected>) => void;
  /** «Nueva subárea» desde el menú del área; sin él (organigrama de la suite), no hay menú. */
  onCreate?: (c: OrgCreating) => void;
  /** «Agregar persona» desde el menú del área. */
  onAddPerson?: (unitId: string) => void;
  /** Solo la estructura (paso «Áreas» de la puesta en marcha): sin personas ni «Agregar persona». */
  onlyAreas?: boolean;
}

/**
 * Árbol de Organización sobre Tree de @xiltrion/ui: áreas y subáreas (sin límite de niveles) y, si se pide, las
 * personas de cada una con lo que hacen ahí (aprueba, firma). Todo empieza abierto; lo que se cierra queda cerrado aunque cambien los datos.
 */
export function OrgTree({ units, activeUsers, users, showPeople, selected, onSelect, onCreate, onAddPerson, onlyAreas }: Props) {
  const nodes = useMemo(() => {
    const m: Record<string, OrgNode> = {};
    /** Quien aprueba o firma primero, luego el resto por nombre. */
    const addPeople = (parentKey: string, unitId: string, list: UserRow[]) =>
      list
        .map((p) => {
          const m = p.memberships.find((x) => x.unitId === unitId);
          const badges = [m?.approves && 'Aprueba', m?.signs && 'Firma', m && !m.responds && 'No responde'].filter((b): b is string => !!b);
          return { p, badges, rank: m?.approves || m?.signs ? 0 : 1 };
        })
        .sort((a, b) => a.rank - b.rank)
        .map(({ p, badges }) => {
          const key = `p:${parentKey}:${p.id}`;
          m[key] = { kind: 'person', name: p.displayName, children: [], user: p, badges };
          return key;
        });
    const addUnit = (u: UnitNode): string => {
      const key = `u:${u.id}`;
      const own = showPeople ? activeUsers.filter((x) => x.unitIds.includes(u.id)) : [];
      m[key] = { kind: 'unit', name: u.name, unit: u, children: [...addPeople(key, u.id, own), ...u.children.map(addUnit)] };
      return key;
    };
    m[ROOT] = { kind: 'root', name: 'Organización', children: units.map(addUnit) };
    return m;
  }, [units, activeUsers, showPeople]);

  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  const folders = useMemo(() => Object.keys(nodes).filter((k) => k !== ROOT && nodes[k]!.children.length > 0), [nodes]);
  const expandedItems = useMemo(() => folders.filter((k) => !closed.has(k)), [folders, closed]);
  const selectedItems = useMemo(() => (selected ? [keyOf(selected)] : []), [selected]);

  const tree = useTree<OrgNode>({
    rootItemId: ROOT,
    indent: INDENT,
    state: { expandedItems, selectedItems },
    setExpandedItems: (next) => {
      const open = new Set(typeof next === 'function' ? next(expandedItems) : next);
      setClosed(new Set(folders.filter((k) => !open.has(k))));
    },
    setSelectedItems: () => { /* la selección la manda `selected` */ },
    onPrimaryAction: (item) => {
      const n = item.getItemData();
      if (n.kind === 'unit') onSelect({ kind: 'unit', id: n.unit.id });
    },
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => item.getItemData().children.length > 0,
    dataLoader: { getItem: (id) => nodes[id]!, getChildren: (id) => nodes[id]?.children ?? [] },
    features: [syncDataLoaderFeature, selectionFeature, hotkeysCoreFeature],
  });
  // Headless Tree guarda su propia lista de filas: se rehace cuando cambian los datos.
  useEffect(() => { tree.rebuildTree(); }, [tree, nodes]);


  return (
    <Tree tree={tree} indent={INDENT} lineas aria-label="Áreas y personas" className="[--tree-fondo:var(--card)]">
      {tree.getItems().map((item) => {
        const n = item.getItemData();
        if (!n || n.kind === 'root') return null;
        // Un clic en el contenido selecciona sin plegar; la flecha sigue abriendo y cerrando.
        const pick = (e: React.MouseEvent) => { e.stopPropagation(); item.setFocused(); item.primaryAction(); };
        if (n.kind === 'person') {
          const u = n.user;
          return (
            <TreeItem key={item.getId()} item={item} asChild>
              <div>
                <TreeItemLabel>
                  <span className="flex min-w-0 flex-1 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Icono nombre="mdi:account-outline" className="size-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{u.displayName}</span>
                    {n.badges.map((b) => <Badge key={b} variant={b === 'No responde' ? 'outline' : 'secondary'}>{b}</Badge>)}
                    <span className={cn('ml-auto shrink-0 text-xs', u.availabilityStatus === 'AVAILABLE' ? 'text-muted-foreground' : 'text-warning')}>{AVAILABILITY_LABEL[u.availabilityStatus] ?? u.availabilityStatus}</span>
                    <span className="size-8 shrink-0" aria-hidden />
                  </span>
                </TreeItemLabel>
              </div>
            </TreeItem>
          );
        }
        const active = n.unit.active;
        const people = users.filter((x) => x.unitIds.includes(n.unit.id)).length;
        const count = `${people} persona${people === 1 ? '' : 's'}`;
        return (
          <TreeItem key={item.getId()} item={item} asChild>
            <div>
              <TreeItemLabel>
                <span className="flex min-w-0 flex-1 cursor-pointer items-center gap-2" onClick={pick}>
                  <Icono nombre="mdi:office-building-outline" className="size-4 shrink-0 text-muted-foreground" />
                  <span className={cn('min-w-[7rem] max-w-[60%] shrink-0 truncate', !active && 'text-muted-foreground line-through')} title={n.name}>{n.name}</span>
                  <span className="min-w-0 shrink-[6] truncate text-xs text-muted-foreground">{n.unit.code}</span>
                  {!active && <Badge variant="secondary">Inactivo</Badge>}
                  {onlyAreas ? <span className="ml-auto" /> : (
                    <span className="ml-auto hidden min-w-0 items-center gap-3 pr-1 text-xs text-muted-foreground md:flex">
                      <span className="shrink-0 tnum">{count}</span>
                    </span>
                  )}
                  {onCreate ? (
                    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon-sm" aria-label={`Agregar dentro de ${n.name}`}><Icono nombre="mdi:plus" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => onCreate({ kind: 'unit', parentId: n.unit.id })}><Icono nombre="mdi:office-building-outline" /> Nueva subárea</DropdownMenuItem>
                          {!onlyAreas && onAddPerson && <DropdownMenuItem onSelect={() => onAddPerson(n.unit.id)}><Icono nombre="mdi:account-plus-outline" /> Agregar persona</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  ) : <span className="size-8 shrink-0" aria-hidden />}
                </span>
              </TreeItemLabel>
            </div>
          </TreeItem>
        );
      })}
    </Tree>
  );
}
